import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import SignaturePad, { SlotFirma } from "./SignaturePad";

const oggi = () => new Date().toISOString().slice(0, 10);

/**
 * Dialog generico per raccogliere firme su tablet.
 * ruoli: [{ key, ruolo, label, titolo }] — key = campo salvato, ruolo = suffisso data-testid
 * valori: oggetto con i campi firma_* iniziali; onSave(payload) deve chiamare l'API.
 */
export default function FirmeDialog({ open, onOpenChange, titolo, descrizione, ruoli, valori, onSave, prefix = "" }) {
  const [form, setForm] = useState({});
  const [pad, setPad] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const init = { firma_luogo: valori?.firma_luogo || "", firma_data: valori?.firma_data || "" };
    ruoli.forEach((r) => { init[r.key] = valori?.[r.key] || ""; });
    setForm(init);
  }, [open, valori, ruoli]);

  const salva = async (payload = form) => {
    setSaving(true);
    try {
      await onSave(payload);
      toast.success("Firme salvate");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio firme");
    } finally {
      setSaving(false);
    }
  };

  const applica = async (dataUrl) => {
    const next = { ...form, [pad.key]: dataUrl, firma_data: form.firma_data || oggi() };
    setForm(next);
    await salva(next);
  };

  const tid = (s) => `${prefix}${s}`;
  const cols = ruoli.length >= 3 ? "sm:grid-cols-3" : ruoli.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-1";

  return (
    <>
      <Dialog open={open && !pad} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl" data-testid={tid("firme-dialog")}>
          <DialogHeader>
            <DialogTitle className="font-heading">{titolo}</DialogTitle>
            <DialogDescription>{descrizione || "Passa il tablet a chi deve firmare: le firme vengono inserite nel PDF."}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Luogo</Label>
              <Input value={form.firma_luogo || ""} onChange={(e) => setForm({ ...form, firma_luogo: e.target.value })} placeholder="es. Pistoia" data-testid={tid("firma-luogo-input")} /></div>
            <div className="space-y-2"><Label>Data firma</Label>
              <Input type="date" value={form.firma_data || ""} onChange={(e) => setForm({ ...form, firma_data: e.target.value })} data-testid={tid("firma-data-input")} /></div>
          </div>
          <div className={`grid grid-cols-1 ${cols} gap-3`}>
            {ruoli.map((r) => (
              <SlotFirma key={r.key} label={r.label} ruolo={`${prefix}${r.ruolo}`} firma={form[r.key]}
                onFirma={() => setPad(r)} onRimuovi={() => setForm({ ...form, [r.key]: "" })} />
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid={tid("firme-chiudi-btn")}>Chiudi</Button>
            <Button type="button" onClick={() => salva()} disabled={saving} data-testid={tid("firme-salva-btn")}>{saving ? "Salvataggio…" : "Salva firme"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SignaturePad
        open={!!pad}
        onOpenChange={(v) => { if (!v) setPad(null); }}
        titolo={pad?.titolo || ""}
        sottotitolo="Firmare nel riquadro. La firma verrà salvata automaticamente."
        onConfirm={applica}
      />
    </>
  );
}
